import { Card, CardContent, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { MoreVertical, Pencil, Trash, UserIcon, Building2 } from "lucide-react";
import { User } from "@/shared/types/userTypes";
import { useMemo } from "react";
import Dropdown from "@/shared/components/Dropdown";
import { getRoleBadgeVariant, getRoleName, OrgType } from "@/shared/types/userRoleTypes";

interface UserCardProps {
    user: User;
    onManageUser?: () => void;
    onDeleteUser?: () => void;
}

const UserCard = ({
    user,
    onManageUser,
    onDeleteUser,
}: UserCardProps) => {

    const sortedOrganizations = useMemo(() => {
        return user.organizations?.sort((a, b) => a.organizationType.localeCompare(b.organizationType)).reverse();
    }, [user]);

    const isPlatformUser = useMemo(() => {
        return (user.availableRegions?.length ?? 0) > 0;
    }, [user]);

    const isTenantUser = useMemo(() => {
        return user.organizations?.some(org => org.organizationType === OrgType.TENANT);
    }, [user]);

    const isCustomerUser = useMemo(() => {
        return user.organizations?.some(org => org.organizationType === OrgType.CUSTOMER);
    }, [user]);

    const hasNoAssociatedOrgs = useMemo(() => {
        return (user.organizations?.length ?? 0) === 0;
    }, [user]);

    const hasOrganizations = useMemo(() => {
        return (user.organizations?.length ?? 0) > 0;
    }, [user]);

    const canManageUser = useMemo(() => {
        return (isTenantUser || hasNoAssociatedOrgs) && !isPlatformUser;
    }, [isTenantUser, hasNoAssociatedOrgs]);

    const canDeleteUser = useMemo(() => {
        return (hasNoAssociatedOrgs) && !isPlatformUser;
    }, [isTenantUser, hasNoAssociatedOrgs]);

    const dropdownMenuItems = useMemo(() => {
        const items = [];
        if (onManageUser && canManageUser) {
            items.push({
                label: 'Manage',
                icon: Pencil,
                variant: 'default',
                onClick: onManageUser,
            });
        }
        if (onDeleteUser && canDeleteUser) {
            items.push({
                label: 'Delete User',
                icon: Trash,
                variant: 'destructive',
                onClick: onDeleteUser,
            });
        }
        return items;
    }, [onManageUser, onDeleteUser, user]);
    return (
        <Card className="flex flex-col gap-4">
            <CardContent className="flex flex-col gap-4 p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-background rounded-lg flex items-center justify-center border">
                            <UserIcon className="h-6 w-6" />
                        </div>
                        <div>
                            <CardTitle className="text-lg">{user.name}</CardTitle>
                            <div className="flex items-center space-x-2">
                                <Badge variant="default">{user.email}</Badge>
                                {user.canAccessGlobalDashboard && (
                                    <Badge variant='default' className="text-xs">
                                        Global Dashboard
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {hasOrganizations ? (
                            <Badge variant="default" className="text-xs">
                                {isCustomerUser ? 'Customer User' : 'Tenant User'}
                            </Badge>
                        ) : isPlatformUser ? (
                            <Badge variant="default" className="text-xs">
                                {'Platform User'}
                            </Badge>
                        ) : null
                        }
                        {/* Action Buttons */}
                        {dropdownMenuItems.length > 0 && (
                            <Dropdown triggerIcon={MoreVertical} items={dropdownMenuItems} />
                        )}
                    </div>
                </div>
                {hasOrganizations ? (
                    <div className="flex flex-col items-start space-y-2">
                        <span className="text-sm font-medium">Associated Account{user.organizations?.length > 1 ? 's' : ''}</span>
                        {sortedOrganizations.map((organization) => (
                            <div key={organization.organizationId} className={`border border-border flex flex-row items-center justify-between gap-2 rounded-lg p-2 w-full ${(organization.isDefault === true ? 'bg-muted' : '')}`}>
                                <div className="flex flex-row items-center gap-2">
                                    <Building2 className="h-4 w-4" />
                                    <p className="text-sm font-medium">{organization.name}</p>
                                </div>
                                <Badge variant={getRoleBadgeVariant(organization.role)} className="text-xs">{getRoleName(organization.role)}</Badge>
                            </div>
                        ))}
                    </div>
                ) : hasNoAssociatedOrgs && !isPlatformUser ? (
                    <div className="flex flex-col items-start space-y-2">
                        <span className="text-sm font-medium text-destructive italic">No associated organizations</span>
                    </div>
                ) : null}
            </CardContent>
        </Card>
    );
};

export default UserCard; 